import React, { memo, useRef } from 'react'

import { classnames, Close, Drag, Icon } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { func, node, string } from 'prop-types'
import { createPortal } from 'react-dom'

import { iconSize } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

import useCornerResize, { CORNERS } from './useCornerResize'
import useDraggable from './useDraggable'
import useFitOnOpen from './useFitOnOpen'

// Initial width, used to place the window before it mounts; keep in sync with
// .detached-window in _detached_window.scss. Users can resize it afterwards.
const WINDOW_WIDTH = 440

// Each corner handle needs its own accessible name so screen readers can tell them apart.
const RESIZE_LABEL_KEYS = {
  'top-left': 'viewerSidebar.enhance.variations.window.resizeTopLeft',
  'top-right': 'viewerSidebar.enhance.variations.window.resizeTopRight',
  'bottom-left': 'viewerSidebar.enhance.variations.window.resizeBottomLeft',
}

function DetachedWindow({ title, onClose: handleClose, children }) {
  const t = useTranslation()
  const windowRef = useRef(null)
  const bodyRef = useRef(null)
  const {
    position,
    setPosition,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleKeyDown,
  } = useDraggable(windowRef, WINDOW_WIDTH)
  const cornerHandlers = useCornerResize(windowRef, position, setPosition)
  useFitOnOpen(windowRef, bodyRef, setPosition)

  return createPortal(
    <section
      ref={windowRef}
      className="detached-window"
      style={{ left: position.x, top: position.y }}
      role="dialog"
      aria-label={title}
    >
      {/* Browsers only offer a resize handle at the bottom-right; the other corners are custom. */}
      {CORNERS.map(corner => (
        <span
          key={corner}
          className={classnames('detached-window__resize', `detached-window__resize--${corner}`)}
          role="button"
          tabIndex={0}
          aria-label={t(RESIZE_LABEL_KEYS[corner])}
          {...cornerHandlers[corner]}
        />
      ))}
      <div
        className="detached-window__header"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <span
          className="detached-window__handle"
          role="button"
          tabIndex={0}
          aria-label={t('viewerSidebar.enhance.variations.window.move')}
          onKeyDown={handleKeyDown}
        >
          <Icon svgComponent={Drag} color="dark" size={iconSize} notSpaced />
        </span>
        <span className="detached-window__title cgt-heading-50">{title}</span>
        <button
          type="button"
          className="detached-window__close"
          aria-label={t('viewerSidebar.enhance.variations.window.close')}
          onClick={handleClose}
        >
          <Icon svgComponent={Close} color="dark" size={iconSize} notSpaced />
        </button>
      </div>
      <div ref={bodyRef} className="detached-window__body">
        {children}
      </div>
    </section>,
    document.body,
  )
}

DetachedWindow.propTypes = {
  title: string.isRequired,
  onClose: func.isRequired,
  children: node.isRequired,
}

export default memo(DetachedWindow)
